/**
 * Shared actions for route points.
 * Used by RoutePointList (toolbar) and by the on-map context menu.
 */
import { requirePermission } from '@/services/permissionService'
import { recalcSegmentsForDelete, rebuildAllSegments } from '@/services/routeEditService'
import { tryDeleteRoutePointViaSession } from '@/services/routeEditSessionService'
import { clearSpinningRoutePoints, setSpinningRoutePoints } from '@/services/routingService'
import { showError, showInfo } from '@/services/toastService'
import { useLibraryStore } from '@/stores/libraryStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { Tour } from '@/types/tour'
import { requireItemEditable } from '@/utils/permissionDialog'
import { getTrackLocalIndexFromGlobalIndex, getTrackRoutePoints } from '@/utils/trackRoutePoints'

/**
 * Delete a route point with segment recalc.
 * Works in both workspace and library contexts.
 */
export async function deleteRoutePointAction(tourId: string, rpIndex: number): Promise<boolean> {
  if (!(await requirePermission('delete_waypoint'))) return false

  const workspaceStore = useWorkspaceStore()
  const libraryStore = useLibraryStore()
  const uiStore = useUiStore()

  const isLibraryContext = !workspaceStore.clientId
  const tour: Tour | undefined = isLibraryContext
    ? libraryStore.detailTour?.clientId === tourId
      ? (libraryStore.detailTour as Tour)
      : undefined
    : workspaceStore.tours.find((t) => t.clientId === tourId)

  if (!tour || rpIndex < 0 || rpIndex >= tour.routePoints.length) return false
  if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return false

  const rp = tour.routePoints[rpIndex]
  const track =
    tour.tracks.find((candidate) => candidate.clientId === (rp.trackClientId ?? null)) ??
    tour.tracks[0]
  const store = isLibraryContext ? libraryStore : workspaceStore
  const hasLocalSegmentData = track?.segments.some((s) => s.points.length > 0) ?? false
  const trackRoutePoints =
    track?.clientId != null
      ? getTrackRoutePoints(tour.routePoints, track.clientId)
      : tour.routePoints
  const trackRoutePointIndex =
    track?.clientId != null
      ? (getTrackLocalIndexFromGlobalIndex(tour.routePoints, track.clientId, rpIndex) ?? rpIndex)
      : rpIndex
  const deletesTrack = !!track && trackRoutePoints.length === 1

  if (trackRoutePoints.length === 2) {
    showInfo('A and B must stay in place. Add another route point before deleting one of them.')
    return false
  }

  const delegatedDelete = await tryDeleteRoutePointViaSession(tourId, rpIndex)
  if (delegatedDelete !== null) return delegatedDelete

  if (deletesTrack && track) {
    if (isLibraryContext) {
      if (!track.clientId) {
        showError('Failed to delete track: missing track identifier')
        return false
      }
      await libraryStore.deleteTrack(tourId, track.clientId)
    } else {
      const trackIndex = tour.tracks.findIndex((candidate) => candidate.clientId === track.clientId)
      if (trackIndex < 0) {
        showError('Failed to delete track: track not found')
        return false
      }
      await workspaceStore.deleteTrack(tourId, trackIndex)
    }

    if (
      uiStore.isTrackEditMode &&
      uiStore.editingTrackTourId === tourId &&
      uiStore.editingTrackClientId === (track.clientId ?? null)
    ) {
      uiStore.exitTrackEditMode()
    }

    return true
  }

  setSpinningRoutePoints([
    trackRoutePoints[trackRoutePointIndex - 1],
    rp,
    trackRoutePoints[trackRoutePointIndex + 1],
  ])
  try {
    if (track && hasLocalSegmentData) {
      try {
        const result = await recalcSegmentsForDelete(
          track.segments,
          trackRoutePoints,
          trackRoutePointIndex,
          uiStore.routingProfile,
        )
        if (result) {
          await store.replaceSegments(
            tourId,
            track.clientId!,
            result.deletedIndices,
            result.newSegments,
          )
        } else {
          showError('Routing failed – could not update route after deletion')
          return false
        }
      } catch (e) {
        showError(`Segment recalculation failed: ${(e as Error).message}`)
        return false
      }
    } else if (track && track.segments.length > 0) {
      try {
        const remainingPoints = trackRoutePoints.filter((_, i) => i !== trackRoutePointIndex)
        const oldSegmentClientIds = track.segments
          .map((s) => s.clientId)
          .filter((id): id is string => !!id)

        if (remainingPoints.length >= 2) {
          const newSegments = await rebuildAllSegments(remainingPoints, uiStore.routingProfile)
          if (newSegments) {
            await store.replaceAllSegments(
              tourId,
              track.clientId!,
              oldSegmentClientIds,
              newSegments,
            )
          }
        } else {
          await store.replaceAllSegments(tourId, track.clientId!, oldSegmentClientIds, [])
        }
      } catch (e) {
        showError(`Segment recalculation failed: ${(e as Error).message}`)
        return false
      }
    }

    if (isLibraryContext) {
      await libraryStore.deleteRoutePoint(tourId, rpIndex)
    } else {
      await workspaceStore.deleteRoutePoint(tourId, rpIndex)
    }
  } finally {
    clearSpinningRoutePoints()
  }

  return true
}
